import { initGA, usePageTracking } from "@/lib/analytics";
import * as Sentry from "@sentry/react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { SidebarProvider } from "@/components/ui/sidebar";
import { AppSidebar } from "@/components/AppSidebar";
import { Topbar } from "@/components/Topbar";
import { ClientViewBanner } from "@/components/ClientViewBanner";
import { ProtectedRoute } from "@/components/auth/ProtectedRoute";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { RequireOnboarding } from "@/components/auth/RequireOnboarding";
import { TenantProvider } from "@/hooks/useTenant";
import { BrandingProvider } from "@/components/BrandingProvider";
import Auth from "./pages/Auth";
import Dashboard from "./pages/Dashboard";
import Onboarding from "./pages/Onboarding";
import NotFound from "./pages/NotFound";
import Agentic from "./pages/Agentic";
import CopilotCIO from "./pages/copilot/CIO";
import CopilotCompliance from "./pages/copilot/Compliance";
import CopilotPortfolio from "./pages/copilot/Portfolio";

// CRM
import Contacts from "./pages/crm/Contacts";
import MeetingsAndNotes from "./pages/crm/MeetingsAndNotes";
import Actions from "./pages/crm/Actions";
import PipelineDeals from "./pages/pipeline/Deals";

// Portfolio
import Positions from "./pages/portfolio/Positions";
import Alternatives from "./pages/portfolio/Alternatives";
import Transactions from "./pages/portfolio/Transactions";
import Analytics from "./pages/portfolio/Analytics";
import Fees from "./pages/portfolio/Fees";
import Liquidity from "./pages/portfolio/Liquidity";
import Exposures from "./pages/portfolio/Exposures";
import Packs from "./pages/portfolio/Packs";
import Valuations from "./pages/portfolio/Valuations";
import RegulatoryReporting from "./pages/portfolio/RegulatoryReporting";
import ReportsList from "./pages/portfolio/ReportsList";
import ReportBuilder from "./pages/portfolio/ReportBuilder";

// Data
import DocumentSources from "./pages/data/DocumentSources";
import Ingestion from "./pages/data/Ingestion";
import DocumentReview from "./pages/data/DocumentReview";

// Settings
import OrganizationSettings from "./pages/settings/Organization";
import EntitiesSettings from "./pages/settings/Entities";
import UsersSettings from "./pages/settings/Users";
import IntegrationsSettings from "./pages/settings/Integrations";
import SecuritySettings from "./pages/settings/Security";
import RequirementsSettings from "./pages/settings/Requirements";
import FundCatalogSettings from "./pages/settings/FundCatalog";
import AuditLogsSettings from "./pages/settings/AuditLogs";
import BrandingSettings from "./pages/settings/Branding";

// Agentic
import Chasers from "./pages/agentic/Chasers";
import Workflows from "./pages/agentic/Workflows";
import Referrals from "./pages/agentic/Referrals";
import PortfolioAgents from "./pages/agentic/PortfolioAgents";
import TaxTracker from "./pages/agentic/TaxTracker";
import MeetingPrep from "./pages/agentic/MeetingPrep";
import CashFlow from "./pages/agentic/CashFlow";
import FeeAudit from "./pages/agentic/FeeAudit";
import CommitmentPacing from "./pages/agentic/CommitmentPacing";
import Reporting from "./pages/agentic/Reporting";

// Firm (MFO)
import FirmDashboard from "./pages/firm/FirmDashboard";
import FamilyClientList from "./pages/firm/FamilyClientList";
import FamilyDetail from "./pages/firm/FamilyDetail";
import GPExposureView from "./pages/firm/GPExposureView";
import FirmCopilot from "./pages/firm/FirmCopilot";
import FirmAgents from "./pages/firm/FirmAgents";
import AcceptInvitation from "./pages/AcceptInvitation";
import FamilyKYC from "./pages/onboarding/FamilyKYC";
import PortfolioImport from "./pages/onboarding/PortfolioImport";

const SentryRoutes = Sentry.withSentryReactRouterV6Routing(Routes);

// Initialize Google Analytics (no-op when VITE_GA_MEASUREMENT_ID is not set)
initGA();

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      refetchOnWindowFocus: false,
    },
  },
});

const AppLayout = ({ children }: { children: React.ReactNode }) => (
  <SidebarProvider>
    <div className="flex min-h-screen w-full">
      <AppSidebar />
      <div className="flex-1 flex flex-col">
        <ClientViewBanner />
        <Topbar />
        <main className="flex-1">
          {children}
        </main>
      </div>
    </div>
  </SidebarProvider>
);

function PageTracker() {
  usePageTracking();
  return null;
}

const App = () => (
  <ErrorBoundary>
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <PageTracker />
        <TenantProvider>
          <BrandingProvider>
          <SentryRoutes>
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          <Route path="/auth" element={<Auth />} />
          <Route path="/accept-invite" element={<AcceptInvitation />} />
          <Route path="/onboarding/kyc/:familyId" element={<FamilyKYC />} />
          <Route path="/onboarding/kyc" element={<FamilyKYC />} />
          <Route
            path="/onboarding/import/:familyId"
            element={
              <ProtectedRoute>
                <AppLayout>
                  <PortfolioImport />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/onboarding"
            element={
              <ProtectedRoute>
                <Onboarding />
              </ProtectedRoute>
            }
          />
          
          {/* Dashboard */}
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <RequireOnboarding>
                  <AppLayout>
                    <Dashboard />
                  </AppLayout>
                </RequireOnboarding>
              </ProtectedRoute>
            }
          />
          
          {/* Firm (MFO only) */}
          <Route
            path="/firm"
            element={
              <ProtectedRoute>
                <RequireOnboarding>
                  <AppLayout>
                    <FirmDashboard />
                  </AppLayout>
                </RequireOnboarding>
              </ProtectedRoute>
            }
          />
          <Route
            path="/firm/families"
            element={
              <ProtectedRoute>
                <RequireOnboarding>
                  <AppLayout>
                    <FamilyClientList />
                  </AppLayout>
                </RequireOnboarding>
              </ProtectedRoute>
            }
          />
          <Route
            path="/firm/families/:familyId"
            element={
              <ProtectedRoute>
                <RequireOnboarding>
                  <AppLayout>
                    <FamilyDetail />
                  </AppLayout>
                </RequireOnboarding>
              </ProtectedRoute>
            }
          />
          <Route
            path="/firm/gp-exposure"
            element={
              <ProtectedRoute>
                <RequireOnboarding>
                  <AppLayout>
                    <GPExposureView />
                  </AppLayout>
                </RequireOnboarding>
              </ProtectedRoute>
            }
          />
          <Route
            path="/firm/copilot"
            element={
              <ProtectedRoute>
                <RequireOnboarding>
                  <AppLayout>
                    <FirmCopilot />
                  </AppLayout>
                </RequireOnboarding>
              </ProtectedRoute>
            }
          />
          <Route
            path="/firm/agents"
            element={
              <ProtectedRoute>
                <RequireOnboarding>
                  <AppLayout>
                    <FirmAgents />
                  </AppLayout>
                </RequireOnboarding>
              </ProtectedRoute>
            }
          />

          {/* CRM */}
          <Route path="/crm" element={<Navigate to="/crm/contacts" replace />} />
          <Route
            path="/crm/contacts"
            element={
              <ProtectedRoute>
                <RequireOnboarding>
                  <AppLayout>
                    <Contacts />
                  </AppLayout>
                </RequireOnboarding>
              </ProtectedRoute>
            }
          />
          <Route
            path="/crm/meetings"
            element={
              <ProtectedRoute>
                <RequireOnboarding>
                  <AppLayout>
                    <MeetingsAndNotes />
                  </AppLayout>
                </RequireOnboarding>
              </ProtectedRoute>
            }
          />
          <Route
            path="/crm/notes"
            element={<Navigate to="/crm/meetings" replace />}
          />
          <Route
            path="/crm/actions"
            element={
              <ProtectedRoute>
                <RequireOnboarding>
                  <AppLayout>
                    <Actions />
                  </AppLayout>
                </RequireOnboarding>
              </ProtectedRoute>
            }
          />
          <Route
            path="/crm/deals"
            element={
              <ProtectedRoute>
                <RequireOnboarding>
                  <AppLayout>
                    <PipelineDeals />
                  </AppLayout>
                </RequireOnboarding>
              </ProtectedRoute>
            }
          />
          
          {/* Portfolio */}
          <Route path="/portfolio" element={<Navigate to="/portfolio/positions" replace />} />
          <Route path="/portfolio/holdings" element={<Navigate to="/portfolio/positions" replace />} />
          <Route
            path="/portfolio/positions"
            element={
              <ProtectedRoute>
                <RequireOnboarding>
                  <AppLayout>
                    <Positions />
                  </AppLayout>
                </RequireOnboarding>
              </ProtectedRoute>
            }
          />
          <Route
            path="/portfolio/alternatives"
            element={
              <ProtectedRoute>
                <RequireOnboarding>
                  <AppLayout>
                    <Alternatives />
                  </AppLayout>
                </RequireOnboarding>
              </ProtectedRoute>
            }
          />
          <Route
            path="/portfolio/transactions"
            element={
              <ProtectedRoute>
                <RequireOnboarding>
                  <AppLayout>
                    <Transactions />
                  </AppLayout>
                </RequireOnboarding>
              </ProtectedRoute>
            }
          />
          <Route
            path="/portfolio/analytics"
            element={
              <ProtectedRoute>
                <RequireOnboarding>
                  <AppLayout>
                    <Analytics />
                  </AppLayout>
                </RequireOnboarding>
              </ProtectedRoute>
            }
          />
          <Route
            path="/portfolio/fees"
            element={
              <ProtectedRoute>
                <RequireOnboarding>
                  <AppLayout>
                    <Fees />
                  </AppLayout>
                </RequireOnboarding>
              </ProtectedRoute>
            }
          />
          <Route
            path="/portfolio/liquidity"
            element={
              <ProtectedRoute>
                <RequireOnboarding>
                  <AppLayout>
                    <Liquidity />
                  </AppLayout>
                </RequireOnboarding>
              </ProtectedRoute>
            }
          />
          <Route
            path="/portfolio/exposures"
            element={
              <ProtectedRoute>
                <RequireOnboarding>
                  <AppLayout>
                    <Exposures />
                  </AppLayout>
                </RequireOnboarding>
              </ProtectedRoute>
            }
          />
          <Route
            path="/portfolio/packs"
            element={
              <ProtectedRoute>
                <RequireOnboarding>
                  <AppLayout>
                    <Packs />
                  </AppLayout>
                </RequireOnboarding>
              </ProtectedRoute>
            }
          />
          <Route
            path="/portfolio/valuations"
            element={
              <ProtectedRoute>
                <RequireOnboarding>
                  <AppLayout>
                    <Valuations />
                  </AppLayout>
                </RequireOnboarding>
              </ProtectedRoute>
            }
          />
          <Route
            path="/portfolio/regulatory-reporting"
            element={
              <ProtectedRoute>
                <RequireOnboarding>
                  <AppLayout>
                    <RegulatoryReporting />
                  </AppLayout>
                </RequireOnboarding>
              </ProtectedRoute>
            }
          />
          <Route
            path="/portfolio/reports"
            element={
              <ProtectedRoute>
                <RequireOnboarding>
                  <AppLayout>
                    <ReportsList />
                  </AppLayout>
                </RequireOnboarding>
              </ProtectedRoute>
            }
          />
          <Route
            path="/portfolio/reports/builder"
            element={
              <ProtectedRoute>
                <RequireOnboarding>
                  <AppLayout>
                    <ReportBuilder />
                  </AppLayout>
                </RequireOnboarding>
              </ProtectedRoute>
            }
          />

          {/* Data */}
          <Route path="/data" element={<Navigate to="/data/ingestion" replace />} />
          <Route
            path="/data/sources"
            element={
              <ProtectedRoute>
                <RequireOnboarding>
                  <AppLayout>
                    <DocumentSources />
                  </AppLayout>
                </RequireOnboarding>
              </ProtectedRoute>
            }
          />
          <Route
            path="/data/ingestion"
            element={
              <ProtectedRoute>
                <RequireOnboarding>
                  <AppLayout>
                    <Ingestion />
                  </AppLayout>
                </RequireOnboarding>
              </ProtectedRoute>
            }
          />
          <Route
            path="/data/review/:documentId"
            element={
              <ProtectedRoute>
                <RequireOnboarding>
                  <AppLayout>
                    <DocumentReview />
                  </AppLayout>
                </RequireOnboarding>
              </ProtectedRoute>
            }
          />
          <Route path="/data/policies" element={<Navigate to="/data/ingestion" replace />} />
          <Route path="/data/exports" element={<Navigate to="/data/ingestion" replace />} />
          
          {/* Agentic - Simplified to Document Chaser */}
          <Route path="/agentic" element={<Navigate to="/agentic/chasers" replace />} />
          <Route
            path="/agentic/chasers"
            element={
              <ProtectedRoute>
                <RequireOnboarding>
                  <AppLayout>
                    <Chasers />
                  </AppLayout>
                </RequireOnboarding>
              </ProtectedRoute>
            }
          />
          <Route
            path="/agentic/workflows"
            element={
              <ProtectedRoute>
                <RequireOnboarding>
                  <AppLayout>
                    <Workflows />
                  </AppLayout>
                </RequireOnboarding>
              </ProtectedRoute>
            }
          />
          <Route
            path="/agentic/referrals"
            element={
              <ProtectedRoute>
                <RequireOnboarding>
                  <AppLayout>
                    <Referrals />
                  </AppLayout>
                </RequireOnboarding>
              </ProtectedRoute>
            }
          />
          <Route
            path="/agentic/portfolio-agents"
            element={
              <ProtectedRoute>
                <RequireOnboarding>
                  <AppLayout>
                    <PortfolioAgents />
                  </AppLayout>
                </RequireOnboarding>
              </ProtectedRoute>
            }
          />
          <Route
            path="/agentic/tax-tracker"
            element={
              <ProtectedRoute>
                <RequireOnboarding>
                  <AppLayout>
                    <TaxTracker />
                  </AppLayout>
                </RequireOnboarding>
              </ProtectedRoute>
            }
          />
          <Route
            path="/agentic/meeting-prep"
            element={
              <ProtectedRoute>
                <RequireOnboarding>
                  <AppLayout>
                    <MeetingPrep />
                  </AppLayout>
                </RequireOnboarding>
              </ProtectedRoute>
            }
          />
          <Route
            path="/agentic/cash-flow"
            element={
              <ProtectedRoute>
                <RequireOnboarding>
                  <AppLayout>
                    <CashFlow />
                  </AppLayout>
                </RequireOnboarding>
              </ProtectedRoute>
            }
          />
          <Route
            path="/agentic/fee-audit"
            element={
              <ProtectedRoute>
                <RequireOnboarding>
                  <AppLayout>
                    <FeeAudit />
                  </AppLayout>
                </RequireOnboarding>
              </ProtectedRoute>
            }
          />
          <Route
            path="/agentic/commitment-pacing"
            element={
              <ProtectedRoute>
                <RequireOnboarding>
                  <AppLayout>
                    <CommitmentPacing />
                  </AppLayout>
                </RequireOnboarding>
              </ProtectedRoute>
            }
          />
          <Route
            path="/agentic/reporting"
            element={
              <ProtectedRoute>
                <RequireOnboarding>
                  <AppLayout>
                    <Reporting />
                  </AppLayout>
                </RequireOnboarding>
              </ProtectedRoute>
            }
          />

          {/* Settings */}
          <Route path="/settings" element={<Navigate to="/settings/organization" replace />} />
          <Route
            path="/settings/organization"
            element={
              <ProtectedRoute>
                <RequireOnboarding>
                  <AppLayout>
                    <OrganizationSettings />
                  </AppLayout>
                </RequireOnboarding>
              </ProtectedRoute>
            }
          />
          <Route path="/settings/families" element={<Navigate to="/settings/organization" replace />} />
          <Route
            path="/settings/entities"
            element={
              <ProtectedRoute>
                <RequireOnboarding>
                  <AppLayout>
                    <EntitiesSettings />
                  </AppLayout>
                </RequireOnboarding>
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings/users"
            element={
              <ProtectedRoute>
                <RequireOnboarding>
                  <AppLayout>
                    <UsersSettings />
                  </AppLayout>
                </RequireOnboarding>
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings/integrations"
            element={
              <ProtectedRoute>
                <RequireOnboarding>
                  <AppLayout>
                    <IntegrationsSettings />
                  </AppLayout>
                </RequireOnboarding>
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings/security"
            element={
              <ProtectedRoute>
                <RequireOnboarding>
                  <AppLayout>
                    <SecuritySettings />
                  </AppLayout>
                </RequireOnboarding>
              </ProtectedRoute>
            }
          />

          <Route
            path="/settings/requirements"
            element={
              <ProtectedRoute>
                <RequireOnboarding>
                  <AppLayout>
                    <RequirementsSettings />
                  </AppLayout>
                </RequireOnboarding>
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings/funds"
            element={
              <ProtectedRoute>
                <RequireOnboarding>
                  <AppLayout>
                    <FundCatalogSettings />
                  </AppLayout>
                </RequireOnboarding>
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings/audit-logs"
            element={
              <ProtectedRoute>
                <RequireOnboarding>
                  <AppLayout>
                    <AuditLogsSettings />
                  </AppLayout>
                </RequireOnboarding>
              </ProtectedRoute>
            }
          />

          {/* Copilot - Consolidated to single "Ask Vintage" page */}
          <Route
            path="/copilot"
            element={
              <ProtectedRoute>
                <RequireOnboarding>
                  <AppLayout>
                    <CopilotPortfolio />
                  </AppLayout>
                </RequireOnboarding>
              </ProtectedRoute>
            }
          />
          {/* Redirect old copilot routes */}
          <Route path="/copilot/cio" element={<Navigate to="/copilot" replace />} />
          <Route path="/copilot/compliance" element={<Navigate to="/copilot" replace />} />
          <Route path="/copilot/portfolio" element={<Navigate to="/copilot" replace />} />
          
          <Route
            path="/settings/branding"
            element={
              <ProtectedRoute>
                <RequireOnboarding>
                  <AppLayout>
                    <BrandingSettings />
                  </AppLayout>
                </RequireOnboarding>
              </ProtectedRoute>
            }
          />

          {/* Redirects from old routes */}
          <Route path="/relations/*" element={<Navigate to="/crm/contacts" replace />} />
          <Route path="/settings/org" element={<Navigate to="/settings/organization" replace />} />
          <Route path="*" element={<NotFound />} />
          </SentryRoutes>
          </BrandingProvider>
        </TenantProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
  </ErrorBoundary>
);

export default App;
